import { useEffect, useMemo, useRef, useState } from "react";
import { Send, Paperclip, Inbox, Plus, Forward, X, CheckCircle2, AlertOctagon, ArrowLeft, Download } from "lucide-react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const PRIORITIES = ["LOW", "NORMAL", "HIGH", "URGENT"];
const PRIORITY_CLS = {
  LOW: "bg-slate-100 text-slate-700 border-slate-300",
  NORMAL: "bg-sky-100 text-sky-800 border-sky-300",
  HIGH: "bg-amber-100 text-amber-800 border-amber-300",
  URGENT: "bg-red-100 text-red-800 border-red-300",
};

// Small helper — turns a File[] into a FormData so both create + reply +
// forward endpoints share the exact same upload shape on the backend.
function buildFD(fields, files) {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
  (files || []).forEach(f => fd.append("files", f));
  return fd;
}

function AttachmentChip({ att }) {
  return (
    <a href={`${API}/files/${att.file_id}?auth=${getToken()}`} target="_blank" rel="noopener noreferrer"
       className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-brand/30 bg-[color:var(--aii-green-tint)] text-brand hover:bg-brand hover:text-white font-semibold"
       data-testid={`ar-att-${att.file_id}`}>
      <Download size={10}/> {att.filename}
    </a>
  );
}

function TeammatePicker({ team, value, onChange }) {
  // Multi-select with checkboxes — value is a Set<string> of user ids.
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const lo = q.toLowerCase();
    return team.filter(u => !lo || u.name?.toLowerCase().includes(lo) || u.role?.toLowerCase().includes(lo));
  }, [team, q]);
  const toggle = (id) => {
    const next = new Set(value);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(next);
  };
  return (
    <div className="border border-[color:var(--aii-border)] rounded-md bg-white">
      <input value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search teammate by name or role…"
        className="w-full px-3 py-2 border-b border-[color:var(--aii-border)] text-sm"
        data-testid="ar-picker-search"/>
      <div className="max-h-40 overflow-y-auto p-1">
        {filtered.map(u => (
          <label key={u.id} className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={value.has(u.id)} onChange={() => toggle(u.id)}
                   data-testid={`ar-picker-${u.id}`}/>
            <span><b>{u.name}</b> <span className="text-[color:var(--aii-muted)]">· {u.role}{u.destination ? ` · ${u.destination}` : ""}</span></span>
          </label>
        ))}
        {filtered.length === 0 && <div className="p-3 text-[11px] text-[color:var(--aii-muted)] italic">No teammate matches.</div>}
      </div>
    </div>
  );
}

function ComposePane({ team, onCancel, onCreated }) {
  const [matter, setMatter] = useState("");
  const [destination, setDestination] = useState("");
  const [city, setCity] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [recipients, setRecipients] = useState(new Set());
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const submit = async () => {
    if (!matter.trim()) return toast.error("Add the matter");
    if (recipients.size === 0) return toast.error("Pick at least one recipient");
    setBusy(true);
    try {
      const fd = buildFD({
        matter, destination, city, priority,
        recipient_ids: Array.from(recipients).join(","),
      }, files);
      const { data } = await api.post("/approval-requests", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Request sent");
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="aii-card p-5 space-y-3" data-testid="ar-compose">
      <div className="flex items-center gap-2">
        <Plus size={16} className="text-brand"/>
        <h3 className="font-display font-semibold text-lg">New approval request</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Destination</label>
          <input value={destination} onChange={e => setDestination(e.target.value)}
                 placeholder="e.g. India" className="input-aii mt-1" data-testid="ar-destination"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">City</label>
          <input value={city} onChange={e => setCity(e.target.value)}
                 placeholder="e.g. Jaipur" className="input-aii mt-1" data-testid="ar-city"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Priority</label>
          <select value={priority} onChange={e => setPriority(e.target.value)} className="input-aii mt-1" data-testid="ar-priority">
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Send to</label>
        <TeammatePicker team={team} value={recipients} onChange={setRecipients}/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Matter</label>
        <textarea value={matter} onChange={e => setMatter(e.target.value)} rows={4}
                  placeholder="What needs approval? Include any context the recipient needs to decide."
                  className="input-aii mt-1" data-testid="ar-matter"/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Attachments (JPG · PDF · DOC · PPT · XLSX — 15 MB each)</label>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="ar-file-input"
                 accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                 onChange={e => setFiles([...files, ...Array.from(e.target.files || [])])}/>
          <button type="button" onClick={() => fileRef.current?.click()}
                  className="text-xs px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
            <Paperclip size={12}/> Add file
          </button>
          {files.map((f, i) => (
            <span key={`att-${f.name}-${f.size}-${f.lastModified}-${i}`}
                  className="text-[11px] bg-slate-100 px-2 py-1 rounded flex items-center gap-1">
              {f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-red-600"><X size={10}/></button>
            </span>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="text-sm px-3 py-2 text-slate-600 hover:text-slate-900">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="ar-submit"
                className="text-sm px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
          <Send size={12} className="inline mr-1"/> {busy ? "Sending…" : "Submit"}
        </button>
      </div>
    </div>
  );
}

function ReplyBox({ reqId, mode = "reply", team, onDone }) {
  // Same box powers reply + forward — the only extra bit for forward is
  // the teammate picker (+ destination/city fields).
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [recipients, setRecipients] = useState(new Set());
  const [destination, setDestination] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const submit = async () => {
    if (mode === "forward" && recipients.size === 0) return toast.error("Pick at least one recipient");
    if (!body.trim() && files.length === 0) return toast.error("Add a message or a file");
    setBusy(true);
    try {
      const url = mode === "forward" ? `/approval-requests/${reqId}/forward` : `/approval-requests/${reqId}/reply`;
      const fd = mode === "forward"
        ? buildFD({ body, destination, city, recipient_ids: Array.from(recipients).join(",") }, files)
        : buildFD({ body }, files);
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(mode === "forward" ? "Forwarded" : "Reply sent");
      setBody(""); setFiles([]); setRecipients(new Set()); setDestination(""); setCity("");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[color:var(--aii-border)] rounded-md p-3 bg-slate-50" data-testid={`ar-${mode}-box`}>
      {mode === "forward" && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input value={destination} onChange={e => setDestination(e.target.value)}
                   placeholder="Destination (optional)" className="input-aii" data-testid="ar-fwd-destination"/>
            <input value={city} onChange={e => setCity(e.target.value)}
                   placeholder="City (optional)" className="input-aii" data-testid="ar-fwd-city"/>
          </div>
          <div className="mb-2">
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">Forward to (Founder / BDOM / anyone)</label>
            <TeammatePicker team={team} value={recipients} onChange={setRecipients}/>
          </div>
        </>
      )}
      <textarea value={body} onChange={e => setBody(e.target.value)} rows={3}
                placeholder={mode === "forward" ? "Add context for the new recipient…" : "Type your reply…"}
                className="input-aii" data-testid={`ar-${mode}-body`}/>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <input ref={fileRef} type="file" multiple className="hidden"
               accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
               data-testid={`ar-${mode}-file-input`}
               onChange={e => setFiles([...files, ...Array.from(e.target.files || [])])}/>
        <button type="button" onClick={() => fileRef.current?.click()}
                className="text-xs px-2.5 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
          <Paperclip size={11}/> Attach
        </button>
        {files.map((f, i) => (
          <span key={`att-${f.name}-${f.size}-${f.lastModified}-${i}`}
                className="text-[11px] bg-slate-100 px-2 py-1 rounded flex items-center gap-1">
            {f.name}
            <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-red-600"><X size={10}/></button>
          </span>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={submit} disabled={busy} data-testid={`ar-${mode}-submit`}
                  className="text-xs px-3 py-1.5 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
            {mode === "forward" ? <><Forward size={11}/> Forward</> : <><Send size={11}/> Reply</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ThreadView({ reqId, team, onBack, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // 'reply' | 'forward'
  const load = () => api.get(`/approval-requests/${reqId}`).then(r => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [reqId]);
  if (!data) return <div className="aii-card p-6 text-sm text-[color:var(--aii-muted)]">Loading…</div>;
  const { request: req, messages } = data;
  const isRaiser = req.raised_by.id === user.id;
  const canClose = isRaiser && req.status !== "closed";
  const closeReq = async () => {
    if (!window.confirm("Close this approval request?")) return;
    await api.post(`/approval-requests/${reqId}/close`);
    toast.success("Closed");
    onChanged?.(); onBack?.();
  };
  return (
    <div className="space-y-3" data-testid="ar-thread">
      <button onClick={onBack} className="text-xs text-brand hover:underline flex items-center gap-1"><ArrowLeft size={11}/> Back to list</button>
      <div className="aii-card p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${PRIORITY_CLS[req.priority] || PRIORITY_CLS.NORMAL}`}>{req.priority}</span>
              {req.status === "closed" && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">✓ Closed</span>}
              {(req.destination || req.city) && (
                <span className="text-[10px] text-[color:var(--aii-muted)]">📍 {[req.destination, req.city].filter(Boolean).join(" · ")}</span>
              )}
            </div>
            <h2 className="font-display font-semibold text-lg mt-1">{req.matter}</h2>
            <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
              From <b>{req.raised_by.name}</b> · to {(req.recipients || []).map(r => r.name).join(", ")}
            </div>
          </div>
          {canClose && (
            <button onClick={closeReq} data-testid="ar-close"
                    className="text-[11px] px-2 py-1 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 flex items-center gap-1">
              <CheckCircle2 size={11}/> Close
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {(messages || []).map(m => (
          <div key={m.id} data-testid={`ar-msg-${m.id}`} className="aii-card p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs">
                <b>{m.from.name}</b> <span className="text-[color:var(--aii-muted)]">· {m.from.role} · {new Date(m.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${m.action === "open" ? "bg-sky-100 text-sky-800" : m.action === "forward" ? "bg-fuchsia-100 text-fuchsia-800" : "bg-slate-100 text-slate-700"}`}>{m.action}</span>
            </div>
            {m.body && <div className="mt-1.5 text-sm whitespace-pre-wrap">{m.body}</div>}
            {(m.attachments || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {m.attachments.map(a => <AttachmentChip key={a.file_id} att={a}/>)}
              </div>
            )}
            {m.action === "forward" && (m.forward_to || []).length > 0 && (
              <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">→ {m.forward_to.map(r => r.name).join(", ")}</div>
            )}
          </div>
        ))}
      </div>
      {req.status !== "closed" && (
        <div className="space-y-2">
          {!mode && (
            <div className="flex gap-2">
              <button onClick={() => setMode("reply")} data-testid="ar-btn-reply"
                      className="text-sm px-3 py-2 bg-brand text-white rounded-md font-semibold hover:bg-brand-dark flex items-center gap-1">
                <Send size={12}/> Reply
              </button>
              <button onClick={() => setMode("forward")} data-testid="ar-btn-forward"
                      className="text-sm px-3 py-2 bg-white border border-[color:var(--aii-border)] rounded-md font-semibold hover:bg-slate-50 flex items-center gap-1">
                <Forward size={12}/> Forward
              </button>
            </div>
          )}
          {mode && (
            <ReplyBox reqId={reqId} mode={mode} team={team}
                      onDone={() => { setMode(null); load(); onChanged?.(); }}/>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApprovalRequests() {
  const { user } = useAuth();
  const [box, setBox] = useState("all"); // inbox | sent | all
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [composing, setComposing] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/approval-requests", { params: { box } }).then(r => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [box]);
  useEffect(() => {
    api.get("/users").then(r => setTeam((r.data || []).filter(u => u.id !== user.id))).catch(() => setTeam([]));
  }, [user.id]);

  const counts = useMemo(() => ({
    inbox: rows.filter(r => (r.recipients || []).some(x => x.id === user.id) && r.status !== "closed").length,
    sent: rows.filter(r => r.raised_by.id === user.id).length,
  }), [rows, user.id]);

  if (openId) return (
    <div className="space-y-4">
      <ThreadView reqId={openId} team={team}
        onBack={() => setOpenId(null)} onChanged={load}/>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="approval-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Team</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Inbox className="text-brand"/> Approval Requests</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Raise, reply and forward approvals — every reply lands right back in this tab.</p>
        </div>
        <button onClick={() => setComposing(true)} data-testid="ar-new"
                className="text-sm px-3 py-2 bg-brand text-white rounded-md font-semibold hover:bg-brand-dark flex items-center gap-1">
          <Plus size={12}/> New request
        </button>
      </div>

      <div className="flex gap-2">
        {[
          { k: "all", label: "All" },
          { k: "inbox", label: `Inbox (${counts.inbox})` },
          { k: "sent", label: `Sent (${counts.sent})` },
        ].map(t => (
          <button key={t.k} onClick={() => setBox(t.k)} data-testid={`ar-tab-${t.k}`}
                  className={`text-xs px-3 py-1.5 rounded-md font-semibold ${box === t.k ? "bg-brand text-white" : "bg-white border border-[color:var(--aii-border)] hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {composing && (
        <ComposePane team={team}
          onCancel={() => setComposing(false)}
          onCreated={() => { setComposing(false); load(); }}/>
      )}

      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)] italic">
            No approval requests yet — hit &quot;New request&quot; to raise one.
          </div>
        )}
        {rows.map(r => {
          const unread = r.recipients?.some(x => x.id === user.id) && r.status !== "closed";
          return (
            <button key={r.id} onClick={() => setOpenId(r.id)} data-testid={`ar-row-${r.id}`}
                    className={`w-full text-left aii-card p-3 hover:border-brand hover:shadow-sm transition ${unread ? "border-l-4 border-l-brand" : ""}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${PRIORITY_CLS[r.priority] || PRIORITY_CLS.NORMAL}`}>{r.priority}</span>
                    {r.status === "closed" && <span className="text-[9px] uppercase font-bold text-emerald-800 border border-emerald-300 bg-emerald-100 px-1.5 py-0.5 rounded">✓ Closed</span>}
                    <div className="text-sm font-semibold truncate">{r.matter}</div>
                  </div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                    From <b>{r.raised_by.name}</b> · to {(r.recipients || []).map(x => x.name).join(", ")}
                    {(r.destination || r.city) ? ` · 📍 ${[r.destination, r.city].filter(Boolean).join(" · ")}` : ""}
                  </div>
                </div>
                <div className="text-[11px] text-[color:var(--aii-muted)] whitespace-nowrap flex items-center gap-1">
                  {(r.attachments || []).length > 0 && <span title={`${r.attachments.length} attachment(s)`}><Paperclip size={11}/></span>}
                  {r.priority === "URGENT" && <AlertOctagon size={12} className="text-red-600"/>}
                  {new Date(r.updated_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
